import { useState } from 'react';

import type { Approvals, PaperEdit } from '../JournalPaper';
import type { JournalInput } from '../safeJournalApi';
import { SHEET_STYLES } from '../sheetModel';
import { SheetField, SheetHead } from '../SheetParts';
import { DOC_SCHEMAS } from './registry';
import { readValues, toFormData, type DocInput, type DocValues } from './schema';
import { DOC_TYPES, type DocType } from './types';

/**
 * 양식 문서 한 장(V574 · DOC-136) — 칸 정의(registry)를 그대로 그린다. 값이 바뀔 때마다 form_data(자기 서술형)와
 * 제목(titleKey 칸)을 일지 초안에 함께 올린다. 결재란 · 사진 · 결재 요청 · PDF 는 일지 편집 창이 맡는다.
 */
export function DocSheet({
  type,
  journal,
  edit,
  approvals,
}: {
  type: DocType;
  journal: Omit<JournalInput, 'siteId'>;
  edit: PaperEdit;
  approvals?: Approvals;
}) {
  const schema = DOC_SCHEMAS[type];
  // 처음 연 값만 form_data 에서 읽고, 그 뒤로는 이 상태가 원본이다(편집 창이 초안을 들고 있다).
  const [doc, setDoc] = useState<DocValues>(() => readValues(schema, journal.formData));

  const commit = (next: DocValues) => {
    setDoc(next);
    const title = schema.titleKey ? (next.values[schema.titleKey] ?? '').trim() || null : journal.title;
    edit({ formData: toFormData(schema, next), title });
  };
  const setValue = (key: string, value: string) => commit({ ...doc, values: { ...doc.values, [key]: value } });
  const setRows = (key: string, rows: Array<Record<string, string>>) => commit({ ...doc, tables: { ...doc.tables, [key]: rows } });

  return (
    <div className="ss-sheet">
      <style>{SHEET_STYLES}</style>
      <SheetHead title={DOC_TYPES[type].label} approvals={approvals} />
      {schema.sections.map((section, at) =>
        section.kind === 'fields' ? (
          <div key={`f-${at}`}>
            {section.label && <div className="ss-section">{section.label}</div>}
            <div className="ss-fields">
              {section.fields.map((field) => (
                <SheetField key={field.key} label={field.label} wide={field.wide || field.kind === 'textarea'}>
                  <Input field={field} value={doc.values[field.key] ?? ''} onChange={(value) => setValue(field.key, value)} className="input" />
                </SheetField>
              ))}
            </div>
          </div>
        ) : (
          <div key={section.key}>
            <div className="ss-section">
              <span>{section.label}</span>
              <button
                type="button"
                className="btn-secondary"
                style={{ fontSize: '12px', padding: '4px 10px' }}
                onClick={() => setRows(section.key, [...(doc.tables[section.key] ?? []), {}])}
              >
                + 줄 추가
              </button>
            </div>
            <table className="ss-grid">
              <colgroup>
                <col style={{ width: '34px' }} />
                {section.columns.map((column) => (
                  <col key={column.key} style={column.width ? { width: column.width } : undefined} />
                ))}
                <col style={{ width: '32px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>No</th>
                  {section.columns.map((column) => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {(doc.tables[section.key] ?? []).map((row, index) => (
                  <tr key={index}>
                    <td className="ss-c">{index + 1}</td>
                    {section.columns.map((column) => (
                      <td key={column.key} className={column.kind === 'check' ? 'ss-c' : undefined}>
                        <Input
                          field={column}
                          value={row[column.key] ?? ''}
                          className="ss-cell"
                          onChange={(value) =>
                            setRows(
                              section.key,
                              (doc.tables[section.key] ?? []).map((one, i) => (i === index ? { ...one, [column.key]: value } : one)),
                            )
                          }
                        />
                      </td>
                    ))}
                    <td className="ss-c">
                      <button
                        type="button"
                        className="ss-x"
                        aria-label="줄 지우기"
                        onClick={() => setRows(section.key, (doc.tables[section.key] ?? []).filter((_, i) => i !== index))}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ),
      )}
    </div>
  );
}

function Input({ field, value, onChange, className }: { field: DocInput; value: string; onChange: (value: string) => void; className: string }) {
  switch (field.kind) {
    case 'textarea':
      return <textarea className={className} rows={field.rows ?? 3} style={{ width: '100%' }} value={value} onChange={(e) => onChange(e.target.value)} />;
    case 'select':
      return (
        <select className={className} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {(field.options ?? []).map((one) => (
            <option key={one} value={one}>
              {one}
            </option>
          ))}
        </select>
      );
    case 'check':
      return <input type="checkbox" checked={value === 'Y'} onChange={(e) => onChange(e.target.checked ? 'Y' : '')} aria-label={field.label} />;
    case 'date':
    case 'time':
    case 'number':
      return <input type={field.kind} className={className} value={value} onChange={(e) => onChange(e.target.value)} />;
    default:
      return <input className={className} value={value} onChange={(e) => onChange(e.target.value)} />;
  }
}
